import { Link } from "react-router-dom";
import {
    HandHeart,
    Users,
    Camera,
    LineChart,
    BadgeCheck,
    ArrowRight,
    Sprout,
    Utensils,
    BookOpen,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import OrganicShapes from "@/components/OrganicShapes";
import { CATEGORY_LIST } from "@/lib/mockData";

const STEPS = [
    {
        n: "01",
        icon: HandHeart,
        title: "Choose a cause",
        body: "Decide where your contribution should go — tree plantation, food drives, or child welfare. You can split it across causes too.",
    },
    {
        n: "02",
        icon: BadgeCheck,
        title: "A public ID is created",
        body: "Before a drive runs, we mint an ID. Trees get TREE-#### · Food drives FOOD-YYYY-## · Child welfare EDU-YYYY-##. Your donation reference is linked privately.",
    },
    {
        n: "03",
        icon: Users,
        title: "We organise on-ground",
        body: "Our field team — with local volunteers, anganwadi workers, schoolteachers — plants, cooks, and distributes.",
    },
    {
        n: "04",
        icon: Camera,
        title: "Updates are published",
        body: "Field photos, dates, locations and notes are attached to the ID. For trees, we add maintenance updates over a year.",
    },
    {
        n: "05",
        icon: LineChart,
        title: "You (and anyone) can track",
        body: "Look up the ID on our Tracker page. No login. Forever public. Certificates can be downloaded any time.",
    },
];

const FAQ = [
    {
        q: "Can I see what happens after I donate?",
        a: "Yes — within 10 working days of your drive being run, your ID will show field photos, dates, and locations. For trees, the timeline extends to one year.",
    },
    {
        q: "What if a tree I sponsored dies?",
        a: "Replacements are mandatory in the first year. The replacement gets a suffixed ID (e.g. 1001 → 1001-A) and both remain trackable.",
    },
    {
        q: "Are my donor details public?",
        a: "No. Public records contain only the contribution data (date, location, photos). Donor names remain private unless you opt-in.",
    },
    {
        q: "How are food and education drives different from trees?",
        a: "They're one-time distributions, not maintained over time. So their record is published once — with photos, beneficiary group, and quantity — and remains permanent.",
    },
];

export default function HowItWorks() {
    return (
        <main data-testid="how-it-works-page" className="bg-white pb-24 relative overflow-hidden">
            {/* Hero — background image */}
            <section
                className="relative pt-32 pb-24 overflow-hidden"
                style={{
                    backgroundImage: "url('/hiw_bg.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-white/65" aria-hidden />
                <OrganicShapes variant="mixed" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        eyebrow="How it works"
                        heading={[
                            "From intent",
                            "to traceable impact.",
                        ]}
                        italicIndex={1}
                        description="A clear, five-step journey that applies to every cause we run. Designed so any contribution — small or large — has a permanent public footprint."
                    />
                </div>
            </section>

            {/* CATEGORIES STRIP */}
            <section
                className="relative py-14 border-b border-seedh-border"
                style={{ background: "linear-gradient(155deg, #FBF8F2 0%, #F4EFE6 55%, #EEF6EE 100%)" }}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {CATEGORY_LIST.map((c) => (
                            <div
                                key={c.key}
                                className={`card-base bg-white p-6 flex items-start gap-4`}
                                data-testid={`how-cat-${c.key}`}
                            >
                                <span className={`w-12 h-12 rounded-full border ${c.tintBorder} ${c.tintBg} flex items-center justify-center flex-shrink-0`}>
                                    <c.icon className={`w-5 h-5 ${c.accentText}`} />
                                </span>
                                <div>
                                    <div className="font-display text-xl text-seedh-text leading-tight">
                                        {c.label}
                                    </div>
                                    <div className="text-xs text-seedh-muted mt-1.5 font-mono tracking-tight">
                                        Public ID format · {c.idPrefix}-####
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="py-24 md:py-32 bg-white relative">
                <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        eyebrow="The five steps"
                        heading={["A journey that's identical", "across every cause."]}
                        italicIndex={1}
                    />

                    <div className="mt-16 relative">
                        {/* vertical line */}
                        <div
                            aria-hidden
                            className="absolute left-6 md:left-9 top-2 bottom-2 w-px bg-gradient-to-b from-seedh-green via-seedh-blue to-seedh-brown opacity-50"
                        />

                        <div className="space-y-10">
                            {STEPS.map((s, i) => (
                                <SectionReveal key={s.n} delay={i * 0.06}>
                                    <div
                                        className="relative pl-16 md:pl-24"
                                        data-testid={`how-step-${i + 1}`}
                                    >
                                        <span className="absolute left-0 top-0 w-12 h-12 md:w-[72px] md:h-[72px] rounded-full bg-white border border-seedh-border flex items-center justify-center shadow-[0_20px_40px_-25px_rgba(4,88,134,0.35)]">
                                            <s.icon className="w-5 h-5 md:w-6 md:h-6 text-seedh-blue" />
                                        </span>
                                        <div className="flex items-baseline gap-4">
                                            <span className="font-display text-3xl md:text-4xl text-seedh-blue/40">
                                                {s.n}
                                            </span>
                                            <h3 className="font-display text-2xl md:text-3xl text-seedh-text leading-tight">
                                                {s.title}
                                            </h3>
                                        </div>
                                        <p className="mt-4 text-base text-seedh-muted leading-relaxed max-w-2xl">
                                            {s.body}
                                        </p>
                                    </div>
                                </SectionReveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32 bg-seedh-surface-beige relative overflow-hidden">
                <OrganicShapes variant="beige" />
                <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        eyebrow="Frequently asked"
                        heading={["Honest answers,", "no fine print."]}
                        italicIndex={1}
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {FAQ.map((f, i) => (
                            <SectionReveal delay={i * 0.08} key={f.q}>
                                <div className="card-base bg-white p-7 h-full">
                                    <div className="font-display text-xl text-seedh-blue leading-tight">
                                        {f.q}
                                    </div>
                                    <p className="text-sm text-seedh-text mt-3 leading-relaxed">
                                        {f.a}
                                    </p>
                                </div>
                            </SectionReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-white text-center">
                <SectionReveal>
                    <h2 className="font-display text-4xl md:text-5xl text-seedh-blue tracking-tight">
                        Let’s turn good intentions into real impact
                    </h2>
                </SectionReveal>
                <SectionReveal delay={0.1}>
                    <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center px-6">
                        <Link
                            to="/donate"
                            className="btn-primary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                        >
                            Choose a Cause
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/tracker"
                            className="btn-outline rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2"
                        >
                            Open the Tracker
                        </Link>
                    </div>
                </SectionReveal>
            </section>
        </main>
    );
}
